import { useEffect, useState } from "react";
import { Plus, Pencil } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SearchableSelect from "@/components/SearchableSelect";

const TYPES = [
  { value: "overhead", label: "Overhead" },
  { value: "warehouse", label: "Warehouse" },
  { value: "tools", label: "Tools" },
  { value: "fleet", label: "Fleet" },
  { value: "repairs", label: "Repairs" },
  { value: "safety", label: "Safety" },
  { value: "general_materials", label: "General Materials" },
  { value: "other", label: "Other" },
];
const typeLabel = (v) => TYPES.find((t) => t.value === v)?.label || v;

const emptyForm = { name: "", type: "other" };

export default function PurchasingContexts() {
  const [contexts, setContexts] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = () => api.get("/contractor/contexts", { params: { include_inactive: true } }).then((r) => setContexts(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setShow(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, type: c.type }); setShow(true); };

  const save = async () => {
    if (!form.name) return toast.error("Name required");
    try {
      if (editing) {
        await api.patch(`/contractor/contexts/${editing.id}`, form);
        toast.success("Purchasing context updated");
      } else {
        await api.post("/contractor/contexts", form);
        toast.success("Purchasing context created");
      }
      setShow(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const toggleActive = async (c) => {
    await api.patch(`/contractor/contexts/${c.id}/active`, { active: !c.active });
    load();
  };

  return (
    <div className="p-6 md:p-8" data-testid="purchasing-contexts-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">Non-Job Purchasing Contexts</h1>
          <p className="text-slate-500 text-sm mt-1">Categories for purchases not tied to a specific job.</p>
        </div>
        <button onClick={openNew} data-testid="new-context-button" className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold">
          <Plus size={14} /> New Context
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full mt-7 text-[13px]" data-testid="contexts-table">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
              <th className="text-left py-2.5">Name</th><th className="text-left py-2.5">Type</th><th className="text-left py-2.5">Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {contexts.map((c) => (
              <tr key={c.id} className={`border-b border-slate-50 ${!c.active ? "opacity-50" : ""}`} data-testid={`context-row-${c.id}`}>
                <td className="py-3 font-medium">{c.name}</td>
                <td className="py-3 text-slate-500">{typeLabel(c.type)}</td>
                <td className="py-3">{c.active ? "Active" : "Inactive"}</td>
                <td className="py-3 text-right space-x-3">
                  <button onClick={() => openEdit(c)} data-testid={`edit-context-${c.id}`} className="text-slate-400 hover:text-sky-600 inline-flex"><Pencil size={14} /></button>
                  <button onClick={() => toggleActive(c)} data-testid={`toggle-context-${c.id}`} className="text-[12px] font-semibold text-sky-600">
                    {c.active ? "Deactivate" : "Activate"}
                  </button>
                </td>
              </tr>
            ))}
            {contexts.length === 0 && (
              <tr><td colSpan={4} className="text-slate-400 text-sm text-center py-10">No purchasing contexts yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent data-testid="context-modal">
          <DialogHeader><DialogTitle>{editing ? "Edit Purchasing Context" : "New Purchasing Context"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name (e.g. Warehouse Stock)" data-testid="context-name-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <SearchableSelect value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="context-type-select" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm">
              {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </SearchableSelect>
            <button onClick={save} data-testid="save-context-button" className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold">{editing ? "Save Changes" : "Create Context"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
